import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { BookOpen, ChevronDown, ListChecks, Map as MapIcon, Pencil, Route } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { cn } from "@/lib/utils";
import { TOP_NAV } from "@/components/layout/navConfig";
import { ROLE_LABELS, STAGES, workflowsFor } from "@/tours/guides";

const start = (moduleId, workflowId) =>
  window.dispatchEvent(new CustomEvent("buildnexa:start-guide", { detail: { moduleId, workflowId } }));
const startJourney = (stageId) =>
  window.dispatchEvent(new CustomEvent("buildnexa:start-guide", { detail: { journey: true, stageId } }));

/** The "Guide" button in the top bar. It opens a short list: the module overview, then step-by-step guides for the
 *  jobs done in this module, with the ones meant for the signed-in user's role first. */
export function GuideMenu({ moduleId }) {
  const { projectId } = useParams();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [stagesOpen, setStagesOpen] = useState(false);
  const boxRef = useRef(null);

  useEffect(() => { setOpen(false); }, [moduleId]);
  useEffect(() => {
    if (!open) return undefined;
    const down = (e) => { if (!boxRef.current?.contains(e.target)) setOpen(false); };
    const esc = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", down);
    document.addEventListener("keydown", esc);
    return () => { document.removeEventListener("mousedown", down); document.removeEventListener("keydown", esc); };
  }, [open]);

  const role = user?.role;
  const label = TOP_NAV.find((t) => t.id === moduleId)?.label || "this module";
  const all = workflowsFor(moduleId, role, true);
  const shown = workflowsFor(moduleId, role, Boolean(projectId));
  const hiddenForNoProject = all.length - shown.length;
  const pick = (workflowId) => { setOpen(false); start(moduleId, workflowId); };
  const pickJourney = (stageId) => { setOpen(false); startJourney(stageId); };

  return (
    <div ref={boxRef} className="relative hidden shrink-0 md:block">
      <button type="button" data-testid="module-guide-button" data-tour="module-guide" title="Guide for this module" aria-label="Guide for this module"
        aria-haspopup="true" aria-expanded={open} onClick={() => setOpen((v) => !v)}
        className="flex h-7 items-center gap-1.5 rounded-lg border border-[#D6657F] bg-[#910029] px-2.5 text-xs font-semibold text-white shadow-sm transition-colors hover:bg-[#A8002F]">
        <BookOpen className="h-3.5 w-3.5" /> <span>Guide</span>
        <ChevronDown className={cn("h-3 w-3 transition-transform", open && "rotate-180")} />
      </button>

      {open && (
        <div aria-label="Guides" data-testid="guide-menu" className="absolute right-0 top-full z-50 mt-2 w-[360px] max-w-[calc(100vw-1rem)] overflow-hidden rounded-xl border border-[#4A5260] bg-[#2E353F] shadow-2xl">
          <div className="border-b border-[#4A5260] px-4 py-3">
            <p className="text-sm font-semibold text-[#ECF4F7]">{label} guide</p>
            <p className="mt-0.5 text-xs text-[#8B9BB8]">{role === "ORG_ADMIN" ? "Every guide for this module." : role ? `Showing what matters for ${ROLE_LABELS[role] || role} first.` : "Choose a guide."}</p>
          </div>
          <div className="max-h-[70vh] overflow-y-auto p-2">
            <button type="button" data-testid="guide-journey" onClick={() => pickJourney(undefined)}
              className="flex w-full items-start gap-3 rounded-lg border border-[#910029] bg-[#910029]/15 px-3 py-2.5 text-left hover:bg-[#910029]/30">
              <Route className="mt-0.5 h-4 w-4 shrink-0 text-[#F0899F]" />
              <span>
                <span className="block text-[13px] font-semibold text-[#ECF4F7]">Start to finish: the whole job</span>
                <span className="block text-xs text-[#C9D3E0]">From setting up the company to closing the project, across every module, in the order the work happens.</span>
              </span>
            </button>
            <button type="button" data-testid="guide-stages-toggle" aria-expanded={stagesOpen} onClick={() => setStagesOpen((v) => !v)}
              className="mt-1 flex w-full items-center gap-2 rounded-lg px-3 py-1.5 text-left text-xs text-[#8B9BB8] hover:bg-[#454D5C]">
              <ChevronDown className={cn("h-3 w-3 transition-transform", stagesOpen && "rotate-180")} /> Or begin at a stage
            </button>
            {stagesOpen && STAGES.map((st, i) => (
              <button key={st.id} type="button" data-testid={`guide-stage-${st.id}`} onClick={() => pickJourney(st.id)}
                className="flex w-full items-center gap-3 rounded-lg py-1.5 pl-6 pr-3 text-left hover:bg-[#454D5C]">
                <span className="grid h-5 w-5 shrink-0 place-items-center rounded-full bg-[#454D5C] text-[10px] font-bold text-[#ECF4F7]">{i + 1}</span>
                <span className="min-w-0 flex-1 truncate text-[13px] text-[#ECF4F7]">{st.title}</span>
                {role !== "ORG_ADMIN" && st.who.includes(role) && <span className="rounded-full bg-[#910029] px-2 py-0.5 text-[10px] font-semibold text-white">You</span>}
              </button>
            ))}

            <button type="button" data-testid="guide-overview" onClick={() => pick(undefined)}
              className="flex w-full items-start gap-3 rounded-lg px-3 py-2.5 text-left hover:bg-[#454D5C]">
              <MapIcon className="mt-0.5 h-4 w-4 shrink-0 text-[#D6657F]" />
              <span>
                <span className="block text-[13px] font-medium text-[#ECF4F7]">Quick overview</span>
                <span className="block text-xs text-[#8B9BB8]">What this module is for and where things are. About a minute.</span>
              </span>
            </button>

            {[["Guides for this module", shown.filter((w) => !w.detail), ListChecks], ["Field by field: the real forms", shown.filter((w) => w.detail), Pencil]].map(([heading, list, Icon]) => list.length > 0 && (
              <div key={heading}>
                <p className="px-3 pb-1 pt-3 text-[10px] font-bold uppercase tracking-widest text-[#6B7A8D]">{heading}</p>
                {list.map((w) => (
                  <button key={w.id} type="button" data-testid={`guide-workflow-${w.id}`} onClick={() => pick(w.id)}
                    className="flex w-full items-start gap-3 rounded-lg px-3 py-2.5 text-left hover:bg-[#454D5C]">
                    <Icon className="mt-0.5 h-4 w-4 shrink-0 text-[#D6657F]" />
                    <span className="min-w-0 flex-1">
                      <span className="flex items-center gap-2 text-[13px] font-medium text-[#ECF4F7]">
                        {w.title}
                        {w.forYou && role !== "ORG_ADMIN" && <span className="rounded-full bg-[#910029] px-2 py-0.5 text-[10px] font-semibold text-white">For your role</span>}
                      </span>
                      <span className="block text-xs text-[#8B9BB8]">{w.summary} · {w.steps.length} steps</span>
                    </span>
                  </button>
                ))}
              </div>
            ))}
            {hiddenForNoProject > 0 && (
              <p className="px-3 py-2 text-xs text-[#8B9BB8]" data-testid="guide-open-a-project">Open a project to see {hiddenForNoProject} more guide{hiddenForNoProject > 1 ? "s" : ""} about working inside a project.</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
